import React, { useState, useEffect, useRef } from 'react';
import { frame1, frame2, frame3, frame4, frame5, orbitLeftLogo } from '../../../../../images';

const OrbitDesign = () => {
    const icons = [
        frame1,
        frame2,
        frame3,
        frame4,
        frame5,
    ];
    const [isAnimating, setIsAnimating] = useState(false);
    useEffect(() => {
        const timer = setTimeout(() => setIsAnimating(true), 100);
        return () => clearTimeout(timer);
    }, []);

    const containerRef = useRef(null);
    const animationRef = useRef(null);

    useEffect(() => {
    let start = null;
    const duration = 60000;
    let frame;

    const animate = (timestamp) => {
        if (!start) start = timestamp;
        const elapsed = timestamp - start;
        const percent = (elapsed % duration) / duration;
        const angleOffset = percent * 2 * Math.PI;

        const container = containerRef.current;
        if (!container) return;

        const width = container.clientWidth;
        const height = container.clientHeight;

        //  Handle very small screens (like 200px)
        const minSize = Math.min(width, height);
        const isSmallScreen = minSize <= 220;

        const centerX = width / 2;
        const centerY = height / 2;

        //  Dynamic orbit radius (safe for 200px)
        const baseRadius = minSize / 2.8;
        const orbitRadius = isSmallScreen
        ? Math.max(baseRadius - 20, 40) // clamp for tiny screens
        : baseRadius;

        icons.forEach((_, index) => {
        const iconElem = container.children[index];
        if (!iconElem) return;

        //  Optional: scale icons on small screens
        if (isSmallScreen) {
            iconElem.style.transformOrigin = "center";
            iconElem.style.scale = "0.7";
        } else {
            iconElem.style.scale = "1";
        }

        const angle = (2 * Math.PI / icons.length) * index + angleOffset;

        const x =
            centerX +
            orbitRadius * Math.cos(angle) -
            iconElem.clientWidth / 2;

        const y =
            centerY +
            orbitRadius * Math.sin(angle) -
            iconElem.clientHeight / 2;

        iconElem.style.transform = `translate(${x}px, ${y}px)`;
        });

        frame = requestAnimationFrame(animate);
        animationRef.current = frame;
    };

    frame = requestAnimationFrame(animate);
    animationRef.current = frame;

    return () => cancelAnimationFrame(frame);
    }, [icons]);

    return (
        <div className="orbit-container">
            <style>{`
        .orbit-wrapper {
          position: relative;
          width: 550px;
          height: 550px;
          display: flex;
          align-items: center;
          justify-content: center;
        }

        .orbit-ring {
          position: absolute;
          border-radius: 50%;
          opacity: 0;
          background: rgba(249, 249, 249, 1);

        }

        .orbit-ring-1 {
          width: 500px;
          height: 500px;
          box-shadow: 0px 0px 50px 0px rgba(0, 0, 0, 0.43) inset;
          animation: ${isAnimating ? 'firstringExpand 2s ease-in 0.2s infinite alternate' : 'none'};
        }
        .orbit-ring-2 {
          width: 400px;
          height: 400px;
          box-shadow: 0px 0px 50px 0px rgba(0, 0, 0, 0.15) inset;
          animation: ${isAnimating ? 'ringExpand 2s ease-in 0.4s infinite alternate' : 'none'};
        }

        .orbit-ring-3 {
          width: 300px;
          height: 300px;
          box-shadow: 0px 0px 50px 0px rgba(0, 0, 0, 0.15) inset;
          animation: ${isAnimating ? 'ringExpand 2s ease-in 0.8s infinite alternate' : 'none'};
        }

        .orbit-ring-4 {
          width: 200px;
          height: 200px;
          box-shadow: 0px 0px 50px 0px rgba(0, 0, 0, 0.15) inset;
          animation: ${isAnimating ? 'ringExpand 2s ease-in 1.2s infinite alternate' : 'none'};
        }
        .orbit-ring-5{
          width: 100px;
          height: 100px;
          box-shadow: 0px 0px 50px 0px rgba(0, 0, 0, 0.15) inset;
          animation: ${isAnimating ? 'ringExpand 2s ease-in 1.6s infinite alternate' : 'none'};
        }
        @keyframes firstringExpand {
        0% {
            transform: scale(1);
            opacity: 0.6;
        }
        100% {
            transform: scale(1);
            opacity: 1;
        }
        }

        @keyframes ringExpand {
        0% {
            transform: scale(0.92);
            opacity: 0.6;
        }
        100% {
            transform: scale(1);
            opacity: 1;
        }
        }


        .center-logo {
          position: absolute;
          width: 90px;
          height: 90px;
          border-radius: 50%;
          display: flex;
          align-items: center;
          justify-content: center;
          box-shadow: 0px 0px 50px 0px rgba(0, 0, 0, 0.15) inset;
          z-index: 100;
          opacity: 0;
          transform: scale(0);
          animation: ${isAnimating ? 'logoAppear 0.6s cubic-bezier(0.34, 1.56, 0.64, 1) 0.8s forwards, pulse 3s ease-in-out infinite' : 'none'};
          animation-delay: 0.8s, 2s;
        }

        @keyframes logoAppear {
          to {
            opacity: 1;
            transform: scale(1.1);
          }
        }

        @keyframes pulse {
          0%, 100% {
            box-shadow: 0px 0px 50px 0px rgba(0, 0, 0, 0.15) inset;
          50% {
            box-shadow: 0px 0px 70px 5px rgba(0, 0, 0, 0.15) inset;
        }

        .logo-icon {
          font-size: 2.5rem;
          font-weight: 700;
          color: white;
          font-family: 'Outfit', sans-serif;
        }

        .orbit-track {
          position: absolute;
          width: 100%;
          height: 100%;
          animation: ${isAnimating ? 'orbitRotate 20s linear infinite' : 'none'};
          animation-delay: 1s;
        }

        @keyframes orbitRotate {
          from {
            transform: rotate(0deg);
          }
          to {
            transform: rotate(360deg);
          }
        }

        .orbit-item {
          position: absolute;
          left: 50%;
          top: 50%;
          transform-origin: 0 0;
          opacity: 0;
          animation: ${isAnimating ? 'itemFadeIn 0.6s ease-out forwards' : 'none'};
        }

        @keyframes itemFadeIn {
          to {
            opacity: 1;
          }
        }

        .orbit-label {
          background: rgba(255, 255, 255, 0.85);
          backdrop-filter: blur(12px);
          padding: 0.75rem 1.4rem;
          border-radius: 12px;
          color: #9ca3af;
          font-size: 0.9rem;
          font-weight: 500;
          white-space: nowrap;
          box-shadow: 
            0 4px 20px rgba(0, 0, 0, 0.08),
            0 0 0 1px rgba(0, 0, 0, 0.05);
          transition: all 0.3s ease;
          cursor: default;
          /* Counter-rotation to keep text upright */
          transform: rotate(calc(-1 * var(--rotation)));
        }

        .orbit-label:hover {
          background: rgba(255, 255, 255, 0.95);
          color: #6b7280;
          transform: rotate(calc(-1 * var(--rotation))) scale(1.05);
          box-shadow: 
            0 8px 30px rgba(0, 0, 0, 0.12),
            0 0 0 1px rgba(0, 0, 0, 0.08);
        }
      `}</style>

            <div className="orbit-wrapper">
                {/* Orbit Rings */}
                <div className="orbit-ring orbit-ring-1"></div>
                <div className="orbit-ring orbit-ring-2"></div>
                <div className="orbit-ring orbit-ring-3"></div>
                <div className="orbit-ring orbit-ring-4"></div>
                <div className="orbit-ring orbit-ring-5"></div>

                {/* Center Logo */}
                <div className="center-logo">
                    <div className="logo-icon"><img src={orbitLeftLogo} className='img-fluid' alt="orbitLeftLogo"></img></div>
                </div>

                {/* Rotating icons */}
                <div
                    ref={containerRef}
                    style={{
                        position: "absolute",
                        top: 0,
                        left: 0,
                        width: "100%",
                        height: "100%",
                    }}
                >
                    {icons.map((icon, idx) => (
                        <div
                            key={idx}
                            style={{
                                position: "absolute",
                                display: "flex",
                                justifyContent: "center",
                                alignItems: "center",
                                filter: "drop-shadow(0px 0px 10px #ffffff)",
                                transition: "transform 0.2s ease-out",
                            }}
                        >
                            <img
                                src={icon}
                                alt={`icon-${idx}`}
                                style={{
                                    width: "auto",
                                    height: "clamp(24px, 4vw, 40px)", //  responsive icon size
                                    objectFit: "contain",
                                }}
                            />
                        </div>
                    ))}
                </div>
            </div>
        </div>
    );
};

export default OrbitDesign;